import userResources from "api/userResources";
import InputField from "components/fields/InputField";
import getSelectClassNames from "components/SelectFieldClasses/SelectFieldClasses";
import React, { useEffect, useState } from "react";
import { Controller } from "react-hook-form"; // Import Controller from react-hook-form
import Select from "react-tailwindcss-select";
import { toast } from "react-toastify";

export default function InvoiceDetailsForm({
  register,
  errors,
  watch,
  setValue,
  control, // Add control as a prop to access useForm control
  clearErrors,
  updateData,
}) {
  const [airlineData, setAirlineData] = useState([]);
  const [airportFromOptions, setAirportFromOptions] = useState([]);
  const [airportToOptions, setAirportToOptions] = useState([]);
  const [searchFrom, setSearchFrom] = useState("");
  const [searchTo, setSearchTo] = useState("");
  const [loadingFrom, setLoadingFrom] = useState(false);
  const [loadingTo, setLoadingTo] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [airlineCode, setAirlineCode] = useState();


  // Get airlines
  const getairLines = async (airlineCode) => {
    try {
      const response = await userResources.airlines(0, 10, "", airlineCode);
      const data = response.data.data.map((item) => ({
        value: item.id,
        label: item?.airline_code + " " + item?.airline_name, // Use item.airline_name if it exists
      }));

      setAirlineData(data);
      if (updateData) {
        setValue("airLine_id", Number(updateData?.airLine_id));
      }
    } catch (error) {
      console.error("Error fetching airline data:", error);
    }
  };

  // Modify your fetchAirportOptions function to return the data
  const fetchAirportOptions = async (query, setter, loader) => {
    if (!query) {
      setter([]);
      return [];
    }

    loader(true);
    try {
      const response = await userResources.airportDataList({ iata: query });
      const data = response.data.data.map((item) => ({
        value: item.id,
        label: item.iata + " " + item.name,
      }));
      setter(data);
      return data;
    } catch (error) {
      toast.error("Failed to fetch airports.");
      return [];
    } finally {
      loader(false);
    }
  };
  useEffect(() => {
    const timer = setTimeout(
      () =>
        fetchAirportOptions(searchFrom, setAirportFromOptions, setLoadingFrom),
      400
    );
    return () => clearTimeout(timer);
  }, [searchFrom]);

  useEffect(() => {
    const timer = setTimeout(
      () => fetchAirportOptions(searchTo, setAirportToOptions, setLoadingTo),
      400
    );
    return () => clearTimeout(timer);
  }, [searchTo]);

  const customStyles = {
    menuButton: ({ isDisabled }) =>
      `flex w-full overflow-hidden cursor-pointer mt-2 text-lg rounded-lg transition-all duration-300 ${
        isDisabled ? "bg-gray-200" : "bg-white hover:border-gray-400"
      }`,
    menu: "absolute top-10 z-10 w-full bg-white shadow-lg border rounded py-1 text-sm text-gray-700",
    listItem: ({ isSelected }) =>
      `px-2 truncate rounded cursor-pointer transition duration-200 ${
        isSelected
          ? "text-white bg-blue-500"
          : "text-gray-600 hover:bg-blue-100 hover:text-blue-500"
      }`,
  };

  const formatOptionLabel = (data) => (
    <li className="mb-1 block cursor-pointer truncate rounded bg-gray-200 px-2 py-2 text-gray-900 transition duration-200">
      {data.label}
    </li>
  );

  // Modify your useEffect for updateData
  useEffect(() => {
    if (updateData?.fromAirport?.id || updateData?.toAirport?.id) {
      const loadInitialData = async () => {
        // Fetch both airports if they exist
        const fromPromise = updateData?.fromAirport?.id
          ? fetchAirportOptions(
              updateData.fromAirport.name,
              setAirportFromOptions,
              setLoadingFrom
            )
          : Promise.resolve([]);

        const toPromise = updateData?.toAirport?.id
          ? fetchAirportOptions(
              updateData.toAirport.name,
              setAirportToOptions,
              setLoadingTo
            )
          : Promise.resolve([]);

        const [fromOptions, toOptions] = await Promise.all([
          fromPromise,
          toPromise,
        ]);

        if (updateData?.fromAirport?.id) {
          const airportOption = {
            value: updateData.fromAirport.id,
            label: updateData.fromAirport.name,
          };

          setAirportFromOptions((prev) => {
            const exists =
              prev.some((a) => a.value === airportOption.value) ||
              fromOptions.some((a) => a.value === airportOption.value);
            return exists ? prev : [...prev, airportOption];
          });

          setValue("travel_from", airportOption.value);
        }

        if (updateData?.toAirport?.id) {
          const airportOption = {
            value: updateData.toAirport.id,
            label: updateData.toAirport.name,
          };

          setAirportToOptions((prev) => {
            const exists =
              prev.some((a) => a.value === airportOption.value) ||
              toOptions.some((a) => a.value === airportOption.value);
            return exists ? prev : [...prev, airportOption];
          });

          setValue("travel_to", airportOption.value);
        }

        setInitialDataLoaded(true);
      };

      loadInitialData();
    }
  }, [updateData]);

  useEffect(() => {
    if (updateData) {
      setAirlineCode(updateData?.airLine?.airline_code);
    } else {
      getairLines();
    }
  }, []);

  useEffect(() => {
    getairLines(airlineCode);
  }, [airlineCode]);

  return (
    <>
      {/* Departure Date */}
      <InputField
        label="Departure Date"
        id="departure_date"
        type="date"
        register={register}
        name="departure_date"
        rules={{ required: "Departure Date is required" }}
        errorMessage={errors.departure_date?.message}
        onChange={() => clearErrors("departure_date")}
        bgAllDark={true}
      />
      {/* Travel From */}
      <div className="bg-transparent">
        <label className="text-sm text-yellow-500">Travel From</label>
        <Controller
          name="travel_from"
          control={control}
          rules={{ required: "Travel From is required" }}
          render={({ field }) => (
            <Select
              {...field}
              value={
                airportFromOptions.find((opt) => opt.value === field.value) ||
                null
              }
              onChange={(selected) => {
                field.onChange(selected.value);
              }}
              onSearchInputChange={(e) => {
                setSearchFrom(e.target.value);
              }}
              options={airportFromOptions}
              isSearchable
              isLoading={
                loadingFrom ||
                (!initialDataLoaded && updateData?.fromAirport?.id)
              }
              isDisabled={!initialDataLoaded && updateData?.fromAirport?.id}
              placeholder="Select..."
              classNames={customStyles}
              formatOptionLabel={formatOptionLabel}
            />
          )}
        />
        {errors.travel_from && (
          <span className="text-xs text-red-500">
            {errors.travel_from.message}
          </span>
        )}
      </div>

      {/* Travel To */}
      <div className="bg-transparent">
        <label className="text-sm text-yellow-500">Travel To</label>
        <Controller
          name="travel_to"
          control={control}
          rules={{ required: "Travel To is required" }}
          render={({ field }) => (
            <Select
              {...field}
              value={
                airportToOptions.find((opt) => opt.value === field.value) ||
                null
              }
              onChange={(selected) => field.onChange(selected.value)}
              onSearchInputChange={(e) => {
                setSearchTo(e.target.value);
              }}
              options={airportToOptions}
              isSearchable
              isLoading={
                loadingTo || (!initialDataLoaded && updateData?.toAirport?.id)
              }
              isDisabled={!initialDataLoaded && updateData?.toAirport?.id}
              placeholder="Select..."
              classNames={customStyles}
              formatOptionLabel={formatOptionLabel}
            />
          )}
        />
        {errors.travel_to && (
          <span className="text-xs text-red-500">
            {errors.travel_to.message}
          </span>
        )}
      </div>

      {/* Preferred Airline */}
      <div>
        <label className="my-2 block text-sm font-medium text-[#ff4e41]">
          Preferred Airline
        </label>
        <Controller
          name="airLine_id"
          control={control} // Use control here instead of register
          defaultValue={watch("airLine_id")} // Default value from watch
          rules={{ required: "Airline is required." }} // Validation rule
          render={({ field }) => (
            <Select
              {...field} // Pass down the field props to manage form state
              value={airlineData.find(
                (source) => source.value === field.value // Controlled value
              )}
              onSearchInputChange={(e) => {
                setAirlineCode(e.target.value);
              }}
              onChange={(selectedOption) => {
                setValue("airLine_id", selectedOption?.value || "");
                clearErrors("airLine_id"); // Clear error when user selects an option
              }}
              options={airlineData}
              isSearchable={true}
              placeholder="Select..."
              classNames={getSelectClassNames({
                isDisabled: false,
                selectProps: {},
                isSelected: false,
                value: watch("airLine_id"),
              })}
              formatOptionLabel={(data) => (
                <li
                  className={`mb-1 block cursor-pointer select-none truncate rounded px-2 py-2 transition duration-200 ${
                    !data.isSelected
                      ? `bg-gray-200 text-gray-900`
                      : `bg-blue-800 text-gray-200`
                  }`}
                >
                  {data.label}
                </li>
              )}
            />
          )}
        />
        {errors?.airticket?.airLine_id && (
          <span className="text-sm text-red-500">
            {errors?.airticket?.airLine_id.message}
          </span>
        )}
      </div>

      {/* GDS Locator */}
      <InputField
        label="GDS Locator"
        id="gds_locator"
        type="text"
        placeholder="Enter locator"
        register={register}
        name="gds_locator"
        // rules={{ required: "GDS Locator is required" }}
        // onChange={() => clearErrors("gds_locator")}
        // errorMessage={errors.gds_locator?.message}
        bgAllDark={true}
      />
    </>
  );
}
